import {
  Combobox,
  useListCollection,
  Span,
  VStack,
  HStack,
  Box,
  Spinner,
  Text,
  IconButton,
  Link,
} from "@chakra-ui/react"
import { ReactNode, useState, useMemo, useEffect, useRef } from "react"
import { LuSearch, LuX, LuRefreshCw, LuChevronDown } from "react-icons/lu"

/**
 * SearchCombobox - A hybrid combobox optimized for Lambda/cold-start APIs
 *
 * Key Features:
 * - Loads first 100 items by default on mount for browsing
 * - Client-side filtering of cached items as user types
 * - Manual search button for API searches when needed
 * - Refresh button to reload initial items
 * - Dropdown stays open during operations
 * - Enter key selects first item (doesn't trigger search)
 * - Results caching to minimize API calls
 *
 * Usage:
 * ```tsx
 * // Standard usage - loads first 100 items, allows browsing and searching
 * <SearchCombobox
 *   queryType="customers"
 *   label="customers"
 *   onItemSelection={setCustomer}
 *   useSearchQuery={useSearchQuery}
 * />
 *
 * // Disable initial load - search only mode
 * <SearchCombobox
 *   queryType="assets"
 *   label="assets"
 *   loadInitialItems={false}
 *   onItemSelection={setAsset}
 *   useSearchQuery={useSearchQuery}
 * />
 *
 * // With static items (no API calls)
 * <SearchCombobox
 *   staticItems={myStaticData}
 *   onItemSelection={handleSelection}
 * />
 * ```
 */

export type ItemBase = {
  id: string
  name: string
}

type ComboboxItemSelection<Item extends ItemBase> = (value: Item | undefined) => void
export type ComboxActions = (props: {
  clear: () => void
  exclude: (id: string) => void
  unexclude: (id: string) => void
}) => ReactNode

// Hook signature that consumers must provide
export type UseSearchQueryHook<Item extends ItemBase> = (params: {
  queryType: string
  searchTerm: string
  debounceMs: number
  enabled: boolean
  allowEmptySearch: boolean
  additionalVariables: Record<string, any>
}) => {
  data: Item[]
  isLoading: boolean
  isFetching: boolean
  error: Error | null
  refetch: () => Promise<any>
}

export interface SearchComboboxProps<Item extends ItemBase> {
  onItemSelection?: ComboboxItemSelection<Item>
  queryType: string
  placeholder?: string
  label?: string
  CreateAction?: () => React.ReactNode
  Actions?: ComboxActions
  itemContent?: (item: Item) => React.ReactNode
  formatSelectedItem?: (item: Item) => string
  disabled?: boolean
  // Static items support for predefined data (bypasses search)
  staticItems?: Item[]
  initialValue?: Item
  // Controlled value (overrides initialValue if provided)
  value?: Item | null
  // Controlled value by ID only (used when full object not available yet, will be resolved from loaded items)
  valueId?: string | null
  // Min characters before search is allowed
  minSearchLength?: number
  // Load initial items on mount (default: true, loads first 100)
  loadInitialItems?: boolean
  // Additional query variables to pass to the search
  additionalVariables?: Record<string, any>
  // Custom filter function for client-side filtering
  customFilter?: (item: Item, searchTerm: string) => boolean
  // Callback to expose methods for adding items to cache and selecting items
  onReady?: (methods: { addItemToCache: (item: Item) => void; selectItem: (item: Item) => void }) => void
  // Auto-select first item when initial items load
  autoSelectFirst?: boolean
  // Hook to use for search queries (must be provided by consumer)
  useSearchQuery?: UseSearchQueryHook<Item>
  // Callback when input value changes
  onInputChange?: (value: string) => void
  // Trigger to refresh data (increment to trigger refresh)
  refreshTrigger?: number
  // Whether the field is required
  required?: boolean
  // Initial excluded item IDs (for items that should not be shown)
  initialExcludedItems?: string[]
}

function SearchCombobox<Item extends ItemBase>(props: SearchComboboxProps<Item>) {
  const {
    onItemSelection,
    queryType,
    placeholder,
    label,
    CreateAction,
    Actions,
    itemContent,
    formatSelectedItem,
    disabled = false,
    staticItems,
    initialValue,
    value: controlledValue,
    valueId: controlledValueId,
    minSearchLength = 2,
    loadInitialItems = true,
    additionalVariables = {},
    customFilter,
    onReady,
    autoSelectFirst = false,
    useSearchQuery,
    onInputChange,
    refreshTrigger,
    required,
    initialExcludedItems = [],
  } = props

  // Use controlled value if provided, otherwise fall back to initialValue
  const effectiveValue = controlledValue !== undefined ? controlledValue : initialValue

  // Determine placeholder text based on required prop
  // If required is undefined, treat as required (matches Field component behavior)
  const effectivePlaceholder = placeholder !== undefined ? placeholder : required !== false ? "Required" : "Optional"

  const [selectedValue, setSelectedValue] = useState<string[]>(effectiveValue ? [effectiveValue.id] : [])
  const [inputValue, setInputValue] = useState(() => {
    if (!effectiveValue) return ""
    if (formatSelectedItem) {
      return formatSelectedItem(effectiveValue) || effectiveValue.name || ""
    }
    return effectiveValue.name || ""
  })
  const [excludedItems, setExcludedItems] = useState<string[]>(initialExcludedItems)
  const [searchTerm, setSearchTerm] = useState("")
  const [hasSearched, setHasSearched] = useState(false)
  const [cachedResults, setCachedResults] = useState<Item[]>([])
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [hasLoadedInitial, setHasLoadedInitial] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [pendingInputRestore, setPendingInputRestore] = useState<string | null>(null)
  const [isSearchButtonTriggered, setIsSearchButtonTriggered] = useState(false)

  // Track previous controlled value ID to prevent unnecessary updates
  const prevControlledValueIdRef = useRef<string | undefined>(controlledValue?.id)

  // Sync internal state when controlled value changes (by ID only)
  useEffect(() => {
    if (controlledValue !== undefined) {
      const newId = controlledValue?.id
      const prevId = prevControlledValueIdRef.current

      // Only update if ID actually changed
      if (newId !== prevId) {
        if (controlledValue) {
          setSelectedValue([controlledValue.id])
          const displayValue = formatSelectedItem ? formatSelectedItem(controlledValue) : controlledValue.name
          setInputValue(displayValue || "")
        } else {
          // Value is explicitly null/undefined - clear the selection
          setSelectedValue([])
          setInputValue("")
        }
        prevControlledValueIdRef.current = newId
      }
    }
  }, [controlledValue?.id, formatSelectedItem]) // Depend on ID only, not entire object

  // Resolve valueId from loaded items (handles race condition when ID is set before items load)
  useEffect(() => {
    if (controlledValueId !== undefined && controlledValue === undefined) {
      if (controlledValueId) {
        // Try to find the item in cached results
        const foundItem = cachedResults.find((item) => item.id === controlledValueId)
        if (foundItem) {
          setSelectedValue([foundItem.id])
          const displayValue = formatSelectedItem ? formatSelectedItem(foundItem) : foundItem.name
          setInputValue(displayValue || "")
        } else {
          // Item not found yet, just set the ID and wait for it to load
          setSelectedValue([controlledValueId])
          setInputValue("") // Will be updated when item loads
        }
      } else {
        // Value is explicitly null - clear the selection
        setSelectedValue([])
        setInputValue("")
      }
    }
  }, [controlledValueId, controlledValue, cachedResults, formatSelectedItem])

  // Update display name when controlled value's ID matches an item in cache
  // This handles the case where value object is provided but name needs updating from fresh data
  const prevDisplayValueRef = useRef("")
  useEffect(() => {
    if (controlledValue && selectedValue.length > 0) {
      const currentId = selectedValue[0]
      const cachedItem = cachedResults.find((item) => item.id === currentId)
      if (cachedItem) {
        const displayValue = formatSelectedItem ? formatSelectedItem(cachedItem) : cachedItem.name
        // Only update if different AND not empty to avoid unnecessary re-renders
        if (displayValue && displayValue !== prevDisplayValueRef.current) {
          setInputValue(displayValue)
          prevDisplayValueRef.current = displayValue
        }
      }
    }
  }, [cachedResults.length, controlledValue?.id, selectedValue, formatSelectedItem]) // Use length instead of entire array

  // Update excluded items when initialExcludedItems changes
  const prevInitialExcludedItemsRef = useRef<string[]>(initialExcludedItems)
  useEffect(() => {
    const prevSorted = [...prevInitialExcludedItemsRef.current].sort()
    const currentSorted = [...initialExcludedItems].sort()
    const hasChanged = JSON.stringify(prevSorted) !== JSON.stringify(currentSorted)

    if (hasChanged) {
      setExcludedItems(initialExcludedItems)
      prevInitialExcludedItemsRef.current = initialExcludedItems
    }
  }, [initialExcludedItems])

  // Expose methods to parent component
  useEffect(() => {
    if (onReady) {
      onReady({
        addItemToCache: (item: Item) => {
          setCachedResults((prev) => {
            // Check if item already exists
            const exists = prev.find((i) => i.id === item.id)
            if (exists) {
              return prev
            }
            // Add to the beginning of the cache
            return [item, ...prev]
          })
        },
        selectItem: (item: Item) => {
          // Update selected value
          setSelectedValue([item.id])

          // Update input display
          const displayValue = formatSelectedItem ? formatSelectedItem(item) : item.name
          setInputValue(displayValue)

          // Close dropdown
          setIsDropdownOpen(false)
        },
      })
    }
  }, [onReady, formatSelectedItem, queryType])


  console.log(!staticItems && (hasSearched || (loadInitialItems && !hasLoadedInitial)))
  // Use search query only if we don't have static items and hook is provided
  const searchQueryResult = useSearchQuery?.({
    queryType,
    searchTerm: searchTerm,
    debounceMs: 0, // No debounce - we control when to search
    enabled: !staticItems && (hasSearched || (loadInitialItems && !hasLoadedInitial)), // Load initial or when search triggered
    allowEmptySearch: !hasSearched && loadInitialItems, // Allow empty search only for initial load, not for searches
    additionalVariables,
  })

  const searchResults = searchQueryResult?.data || []
  const isLoading = searchQueryResult?.isLoading || false
  const isFetching = searchQueryResult?.isFetching || false
  const error = searchQueryResult?.error || null
  const refetch = searchQueryResult?.refetch || (async () => { })

  console.log(searchQueryResult, "searchQueryResult")


  // Refresh data when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0) {
      refetch()
    }
  }, [refreshTrigger]) // Remove refetch from deps to prevent infinite loops

  // Cache results when they come in
  const prevSearchResultsRef = useRef<Item[]>([])
  useEffect(() => {
    if (!staticItems) {
      // Check if searchResults actually changed (deep comparison by IDs)
      const prevIds = prevSearchResultsRef.current?.map((r) => r.id).sort() || []
      const currentIds = searchResults.map((r) => r.id).sort()
      const hasResultsChanged =
        prevIds.length !== currentIds.length || prevIds.some((id, index) => id !== currentIds[index])

      if (!hasResultsChanged) {
        return // No actual change, skip the effect
      }

      prevSearchResultsRef.current = searchResults as Item[]

      // Mark initial load as complete if we haven't searched yet
      if (!hasSearched && loadInitialItems) {
        setHasLoadedInitial(true)
      }

      setCachedResults((prev) => {
        // If this is a search, replace cached results. If initial load, just set them.
        if (hasSearched) {
          // Merge search results with existing cache
          const merged = [...prev]
          searchResults.forEach((result: any) => {
            if (!merged.find((item) => item.id === result.id)) {
              merged.push(result as Item)
            }
          })
          return merged
        } else {
          // Initial load - just set the results
          return searchResults as Item[]
        }
      })
    }
  }, [searchResults, staticItems, hasSearched, loadInitialItems])

  // Load initial items on mount if requested
  useEffect(() => {
    if (loadInitialItems && !staticItems && !hasLoadedInitial) {
      setSearchTerm("")
      // Don't set hasSearched here - this is the initial load
      // Don't open dropdown by default - let user open it when ready
    }
  }, [loadInitialItems, staticItems, hasLoadedInitial])

  // Keep dropdown open during search/refresh loading states
  useEffect(() => {
    if (hasSearched && isFetching) {
      setIsDropdownOpen(true)
    }
  }, [hasSearched, isFetching])

  // Restore input value if Chakra cleared it after search button click
  useEffect(() => {
    if (pendingInputRestore && inputValue === "") {
      setInputValue(pendingInputRestore)
      setPendingInputRestore(null)
    }
  }, [pendingInputRestore, inputValue])

  // Determine which items to display with client-side filtering
  const items = useMemo(() => {
    let sourceItems: Item[]

    if (staticItems) {
      sourceItems = staticItems
    } else if (hasSearched && searchResults.length > 0) {
      // Merge search results with cached items (deduplicated)
      const merged = [...cachedResults]
      searchResults.forEach((result: any) => {
        if (!merged.find((item) => item.id === result.id)) {
          merged.push(result as Item)
        }
      })
      sourceItems = merged
    } else {
      // Use cached results (initial load)
      sourceItems = cachedResults
    }

    // Client-side filter if user has typed something
    if (inputValue && inputValue.trim() && !staticItems) {
      const searchLower = inputValue.toLowerCase()
      sourceItems = sourceItems.filter((item) => {
        // Use custom filter if provided, otherwise default to name search
        if (customFilter) {
          return customFilter(item, searchLower)
        }
        return item.name.toLowerCase().includes(searchLower)
      })
    }

    return sourceItems.filter((item) => !excludedItems.includes(item.id))
  }, [staticItems, searchResults, cachedResults, excludedItems, hasSearched, inputValue, customFilter])

  const { collection, set } = useListCollection<Item>({
    initialItems: [],
    groupBy: (item) => item.name,
    itemToString: (item) => (formatSelectedItem ? formatSelectedItem(item) : item.name),
    itemToValue: (item) => item.id,
  })

  // Create a stable key from items to detect real changes
  const itemsKey = useMemo(() => {
    return items.map((item) => item.id).join(",")
  }, [items])

  const prevItemsKeyRef = useRef<string>("")

  // Update collection only when items actually change (by ID)
  useEffect(() => {
    if (itemsKey !== prevItemsKeyRef.current) {
      set(items)
      prevItemsKeyRef.current = itemsKey
    }
  }, [itemsKey, items, set])

  // Handle initial value selection
  useEffect(() => {
    if (initialValue) {
      const foundItem = items.find((item) => item.id === initialValue.id)
      if (foundItem) {
        setSelectedValue([foundItem.id])
        setInputValue(formatSelectedItem ? formatSelectedItem(foundItem) || foundItem.name : foundItem.name)
      } else {
        // If not found in items, add it to cached results and select it
        setCachedResults((prev) => {
          if (!prev.find((item) => item.id === initialValue.id)) {
            return [...prev, initialValue]
          }
          return prev
        })
        setSelectedValue([initialValue.id])
        setInputValue(formatSelectedItem ? formatSelectedItem(initialValue) || initialValue.name : initialValue.name)
      }
    }
  }, [initialValue, formatSelectedItem])

  // Auto-select first item when items load (if enabled)
  const hasAutoSelectedRef = useRef(false)
  useEffect(() => {
    if (
      autoSelectFirst &&
      !hasAutoSelectedRef.current &&
      items.length > 0 &&
      !initialValue &&
      selectedValue.length === 0
    ) {
      const firstItem = items[0]
      setSelectedValue([firstItem.id])
      setInputValue(formatSelectedItem ? formatSelectedItem(firstItem) || firstItem.name : firstItem.name)
      if (onItemSelection) {
        onItemSelection(firstItem)
      }
      hasAutoSelectedRef.current = true
    }
  }, [autoSelectFirst, items, initialValue, selectedValue, formatSelectedItem, onItemSelection])

  const handleInputChange = (details: Combobox.InputValueChangeDetails) => {
    setInputValue(details.inputValue)
    onInputChange?.(details.inputValue)
  }

  const handleValueChange = (details: any) => {
    const selectedItem = details.items[0] || undefined

    if (onItemSelection) {
      onItemSelection(selectedItem)
    }
    setSelectedValue(details.value)

    // Update input value to show selected item
    if (selectedItem) {
      const displayValue = formatSelectedItem
        ? formatSelectedItem(selectedItem) || selectedItem.name
        : selectedItem.name
      setInputValue(displayValue)
    }

    // Close dropdown when item is selected
    setIsDropdownOpen(false)
  }

  const handleOpenChange = (details: Combobox.OpenChangeDetails) => {
    // Don't allow opening dropdown if an item is selected
    if (hasSelection) {
      setIsDropdownOpen(false)
      return
    }

    // Ignore close requests from Chakra when we're actively searching
    if (!details.open && (isSearchButtonTriggered || isFetching)) {
      return
    }

    setIsDropdownOpen(details.open)
  }

  const handleClear = () => {
    setSelectedValue([])
    setInputValue("")
    if (onItemSelection) {
      onItemSelection(undefined)
    }
  }

  const handleExclude = (id: string) => {
    if (!excludedItems.includes(id)) {
      setExcludedItems([...excludedItems, id])
    }
  }

  const handleUnexclude = (id: string) => {
    if (excludedItems.includes(id)) {
      setExcludedItems(excludedItems.filter((itemId) => itemId !== id))
    }
  }

  const handleSearch = (e?: React.MouseEvent, capturedValue?: string) => {
    // Use captured value if provided (from onMouseDown), otherwise use current state
    const searchValue = capturedValue || inputValue

    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }

    if (!staticItems && searchValue.length >= minSearchLength) {
      setIsSearchButtonTriggered(true)
      setSearchTerm(searchValue)
      setHasSearched(true)
      // Keep dropdown open during search
      setIsDropdownOpen(true)
      // Mark that we need to restore the input value (Chakra will clear it asynchronously)
      if (capturedValue) {
        setPendingInputRestore(capturedValue)
      }
      // Reset the flag after a short delay to allow the search to complete
      setTimeout(() => setIsSearchButtonTriggered(false), 500)
    }
  }

  const handleRefresh = async () => {
    if (!staticItems) {
      setIsRefreshing(true)
      setSearchTerm("")
      setHasSearched(false)
      setHasLoadedInitial(false)
      // Keep dropdown open during refresh
      setIsDropdownOpen(true)

      // Trigger refetch
      try {
        await refetch()
      } finally {
        setIsRefreshing(false)
      }
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Enter key should select first item if dropdown is open, not trigger search
    if (e.key === "Enter") {
      e.preventDefault()
      // If dropdown is closed, open it
      if (!isDropdownOpen) {
        setIsDropdownOpen(true)
      }
      // Let Chakra handle selection of first filtered item
    }
  }

  const handleClearSearch = () => {
    setInputValue("")
    setSearchTerm("")
    setHasSearched(false)
    setSelectedValue([])
    // Keep dropdown open when clearing
    setIsDropdownOpen(true)
    if (onItemSelection) {
      onItemSelection(undefined)
    }
  }

  // Determine states
  const hasSelection = selectedValue.length > 0
  const isInitialLoad = !staticItems && !hasLoadedInitial && loadInitialItems && isLoading
  const inputLength = inputValue?.length || 0
  const canSearch = !staticItems && inputLength >= minSearchLength
  const isSearching = !staticItems && isFetching && hasSearched && !isRefreshing
  const isAnyLoading = isInitialLoad || isSearching || isRefreshing // Unified loading state
  const showSearchButton = !staticItems && inputLength > 0 && !hasSelection
  const showRefreshButton = !staticItems && inputLength === 0 && !isRefreshing && !hasSelection
  const showClearButton =
    (staticItems && inputLength > 0) || (!staticItems && inputLength > 0 && !showSearchButton) || hasSelection // Show clear in static mode OR when NOT showing search OR when item is selected
  const showEmptyState = !isAnyLoading && collection.items.length === 0
  const showMinLengthHint =
    !staticItems && isDropdownOpen && inputLength > 0 && inputLength < minSearchLength && !hasSearched

  // Disable during initial load (when loading items for the first time)
  const isInitialLoading = loadInitialItems && !hasLoadedInitial && isLoading && !staticItems
  const isDisabled = disabled || (!!error && !staticItems) || isInitialLoading

  // Show "Loading..." placeholder when loading, otherwise use the effective placeholder
  const finalPlaceholder = isAnyLoading ? "Loading..." : effectivePlaceholder

  const isEmpty = collection.items.length === 0
  const Wrapper = isEmpty ? Combobox.Empty : Box

  return (
    <Combobox.Root
      variant={"subtle"}
      positioning={{ strategy: "fixed", hideWhenDetached: true }}
      collection={collection}
      onInputValueChange={handleInputChange}
      onOpenChange={handleOpenChange}
      open={isDropdownOpen}
      inputBehavior="autocomplete"
      placeholder={finalPlaceholder}
      disabled={isDisabled}
      onValueChange={handleValueChange}
      value={selectedValue}
      inputValue={inputValue}
      onKeyDown={handleKeyDown as any}
    >
      <Combobox.Control display="flex">
        <Combobox.Input name={`${queryType}Id`} />
        <Combobox.IndicatorGroup>
          {/* Clear button when there's input or selection */}
          {!disabled && showClearButton && !isSearching && !isRefreshing && (
            <IconButton
              variant="hydrologiq_icon"
              size="xs"
              onClick={handleClearSearch}
              aria-label="Clear"
              type="button"
              title={hasSelection ? "Clear selection" : "Clear input"}
            >
              <LuX />
            </IconButton>
          )}

          {/* Loading indicator during search */}
          {isSearching && (
            <Box display="flex" alignItems="center" paddingX="2">
              <Spinner size="sm" colorPalette="blue" aria-label="Searching" title="Searching..." />
            </Box>
          )}

          {/* Loading indicator during refresh */}
          {isRefreshing && (
            <Box display="flex" alignItems="center" paddingX="2">
              <Spinner size="sm" colorPalette="blue" aria-label="Refreshing" title="Refreshing..." />
            </Box>
          )}

          {/* Search button when user has typed */}
          {!disabled &&
            showSearchButton &&
            !isSearching &&
            !isRefreshing &&
            (() => {
              // Capture the input value at the time the button is rendered
              const capturedInputValue = inputValue

              return (
                <IconButton
                  variant="hydrologiq_icon"
                  size="xs"
                  onMouseDown={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    // Trigger search immediately in onMouseDown before Chakra can clear the input
                    handleSearch(e, capturedInputValue)
                  }}
                  disabled={!canSearch}
                  aria-label="Search"
                  type="button"
                  title={
                    canSearch ? "Search API for more results" : `Enter at least ${minSearchLength} characters to search`
                  }
                >
                  <LuSearch />
                </IconButton>
              )
            })()}

          {/* Refresh button when no input */}
          {!disabled && showRefreshButton && (
            <IconButton
              variant="hydrologiq_icon"
              size="xs"
              onClick={handleRefresh}
              aria-label="Refresh"
              type="button"
              title="Reload items"
            >
              <LuRefreshCw />
            </IconButton>
          )}

          {/* Dropdown trigger - hide when item is selected or disabled */}
          {!disabled && !hasSelection && (
            <Combobox.Trigger asChild>
              <IconButton variant="hydrologiq_icon" size="xs" aria-label="Toggle suggestions" type="button">
                <LuChevronDown />
              </IconButton>
            </Combobox.Trigger>
          )}

          {/* Custom actions */}
          {Actions &&
            Actions({
              clear: handleClear,
              exclude: handleExclude,
              unexclude: handleUnexclude,
            })}
        </Combobox.IndicatorGroup>
      </Combobox.Control>

      <Combobox.Positioner>
        <Combobox.Content maxHeight="240px" overflowY="auto">
          {/* Show unified loading state */}
          {isAnyLoading && (
            <Box padding="6" display="flex" alignItems="center" justifyContent="center">
              <VStack gap="3">
                <Spinner size="lg" colorPalette="blue" aria-label="Loading" />
                <VStack gap="1">
                  <Text textStyle="sm" fontWeight="medium">
                    {isRefreshing
                      ? `Refreshing ${label || "items"}...`
                      : isSearching
                        ? `Searching ${label || "items"}...`
                        : `Loading ${label || "items"}...`}
                  </Text>
                  {(isSearching || isInitialLoad) && (
                    <Text textStyle="xs" color="fg.muted">
                      This may take a moment
                    </Text>
                  )}
                </VStack>
              </VStack>
            </Box>
          )}

          {/* Show error state */}
          {error && !staticItems && (
            <Box padding="4" display="flex" alignItems="center" justifyContent="center">
              <VStack gap="2">
                <Text textStyle="sm" color="fg.error" fontWeight="medium">
                  Error loading {label || "items"}
                </Text>
                <Text textStyle="xs" color="fg.muted">
                  Please try again
                </Text>
              </VStack>
            </Box>
          )}

          {/* Show minimum length hint */}
          {showMinLengthHint && (
            <Box padding="4" display="flex" alignItems="center" justifyContent="center">
              <Text textStyle="sm" color="fg.muted">
                Enter at least {minSearchLength} characters to search
              </Text>
            </Box>
          )}

          <Wrapper>
            <HStack width="100%" justifyContent="space-between" padding="2">
              {(hasSearched || inputValue?.trim()) && (
                <Link variant="primary" onClick={handleSearch} _hover={{ textDecoration: "underline" }}>
                  Search all {label || "items"}
                </Link>
              )}
              {CreateAction && <CreateAction />}
            </HStack>
          </Wrapper>

          {/* Show items */}
          {!isAnyLoading &&
            collection.items.map((item) => (
              <Combobox.Item item={item} key={item.id} cursor="pointer">
                <VStack gap={0} align="stretch">
                  {itemContent ? (
                    itemContent(item)
                  ) : (
                    <Span textStyle="sm" fontWeight="medium">
                      {item.name}
                    </Span>
                  )}
                </VStack>
                <Combobox.ItemIndicator />
              </Combobox.Item>
            ))}
        </Combobox.Content>
      </Combobox.Positioner>
    </Combobox.Root>
  )
}

export default SearchCombobox
